import { Component } from '@theme/component';
import { nextTick } from '@theme/utilities';

/**
 * Tooltip Component
 * 
 * A universal tooltip component that can be reused throughout the theme.
 * 
 * @typedef {object} TooltipRefs
 * @property {HTMLElement} trigger - The element that triggers the tooltip
 * @property {HTMLElement} content - The tooltip content container
 * @property {HTMLButtonElement} [closeButton] - Optional close button
 * @extends Component<TooltipRefs>
 */
class TooltipComponent extends Component {
  requiredRefs = ['trigger', 'content'];

  /** @type {boolean} */
  #isOpen = false;

  /** @type {((event: Event) => void) | null} */
  #documentClickHandler = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupEventListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupEventListeners();
  }

  #setupEventListeners() {
    const { trigger } = this.refs;

    trigger.addEventListener('click', this.#onTriggerClick);
    trigger.addEventListener('touchend', this.#onTriggerTouch);

    const closeButton = this.refs.closeButton;

    if (closeButton) {
      closeButton.addEventListener('click', this.close);
    }
  }

  #cleanupEventListeners() {
    const { trigger } = this.refs;
    
    trigger.removeEventListener('click', this.#onTriggerClick);
    trigger.removeEventListener('touchend', this.#onTriggerTouch);

    const closeButton = this.refs.closeButton;

    if (closeButton) {
      closeButton.removeEventListener('click', this.close);
    }

    if (this.#documentClickHandler) {
      document.removeEventListener('click', this.#documentClickHandler);
      this.#documentClickHandler = null;
    }
  }

  /**
   * Handles trigger click
   * @param {Event} event
   */
  #onTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  };

  /**
   * Handles trigger touch
   * @param {Event} event
   */
  #onTriggerTouch = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  };

  toggle = () => {
    if (this.#isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    if (this.#isOpen) return;

    const { content } = this.refs;
    content.classList.add('tooltip-content--visible');
    this.#isOpen = true;

    nextTick(() => {
      if (!this.#isOpen || !this.isConnected) return;
      
      const handler = this.#onDocumentClick.bind(this);
      this.#documentClickHandler = handler;
      document.addEventListener('click', handler);
    });
  };

  /**
   * Closes the tooltip
   * @param {Event} [event] - Optional event object
   */
  close = (event) => {
    if (!this.#isOpen) return;

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const { content } = this.refs;
    content.classList.remove('tooltip-content--visible');
    this.#isOpen = false;

    if (this.#documentClickHandler) {
      document.removeEventListener('click', this.#documentClickHandler);
      this.#documentClickHandler = null;
    }
  };

  /**
   * Handles document click to close tooltip
   * @param {Event} event
   */
  #onDocumentClick(event) {
    const { content } = this.refs;
    
    // Close if clicking outside tooltip
    if (event.target instanceof Node && !this.contains(event.target)) {
      this.close();
    }
  }
}

if (!customElements.get('tooltip-component')) {
  customElements.define('tooltip-component', TooltipComponent);
}

export default TooltipComponent;

